import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/constants/testIds";
import { useAuthStore } from "@/store";
import { detectIncognitoMode, prepareForOAuthLogin } from "@/utils/auth";
import { ChevronRight, Info, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export function LoginForm({ className, ...props }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loginWithEmail,
    loginWithGitHub,
    loginWithGoogle,
    loginWithLinkedIn,
    checkIncognitoMode,
    isLoading,
  } = useAuthStore();

  const [email, setEmail] = useState("");
  const [error, setError] = useState(location.state?.error || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIncognito, setIsIncognito] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const detectIncognito = async () => {
      try {
        // Try to use the store's method if available
        if (checkIncognitoMode) {
          const result = await checkIncognitoMode();
          setIsIncognito(result);
        } else {
          // Fallback to direct detection
          const result = await detectIncognitoMode();
          setIsIncognito(result);
        }
      } catch (error) {
        console.error("Error detecting incognito mode:", error);
      }
    };

    detectIncognito();
  }, [checkIncognitoMode]);

  // Clear error when location changes
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);

      // Auto-clear the error after 8 seconds
      const timer = setTimeout(() => {
        setError(null);
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleMagicLinkLogin = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (!isValidEmail(email)) {
      setError(
        "Please enter a valid email address with a proper domain (e.g., .com)"
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const {
        success,
        error,
        magicLinkSent: linkSent,
      } = await loginWithEmail(email);

      if (!success) {
        setError(error?.message || "Failed to send magic link");
        return;
      }

      if (linkSent) {
        setMagicLinkSent(true);
        // Don't navigate yet - user needs to click the magic link in their email
      }
    } catch (error) {
      console.error("Error in magic link login process:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGitHubLogin = async () => {
    try {
      // Prepare browser for OAuth login - cleans up any existing OAuth state
      await prepareForOAuthLogin();

      // Start the GitHub OAuth flow
      const result = await loginWithGitHub();

      if (!result.success) {
        throw new Error(
          result.error?.message || "Failed to connect with GitHub"
        );
      }

      // Note: For OAuth, the redirect will be handled by the OAuth callback
      // The callback will then redirect based on onboarding status
    } catch (error) {
      console.error("GitHub login error:", error);
      setError(error.message || "Failed to connect with GitHub");
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Prepare browser for OAuth login
      await prepareForOAuthLogin();

      // Start the Google OAuth flow
      const result = await loginWithGoogle();

      if (!result.success) {
        throw new Error(
          result.error?.message || "Failed to connect with Google"
        );
      }

      // Note: For OAuth, the redirect will be handled by the OAuth callback
      // The callback will then redirect based on onboarding status
    } catch (error) {
      console.error("Google login error:", error);
      setError(error.message || "Failed to connect with Google");
    }
  };

  const handleLinkedInLogin = async () => {
    try {
      // Prepare browser for OAuth login
      await prepareForOAuthLogin();

      // Start the LinkedIn OAuth flow
      const result = await loginWithLinkedIn();

      if (!result.success) {
        throw new Error(
          result.error?.message || "Failed to connect with LinkedIn"
        );
      }

      // Note: For OAuth, the redirect will be handled by the OAuth callback
      // The callback will then redirect based on onboarding status
    } catch (error) {
      console.error("LinkedIn login error:", error);
      setError(error.message || "Failed to connect with LinkedIn");
    }
  };

  if (magicLinkSent) {
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
            <Mail className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold">Check your email</h1>
          <p className="text-balance text-sm text-muted-foreground">
            We&apos;ve sent a magic link to <strong>{email}</strong>
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Click the link in your email to sign in. The link will expire in 1
            hour.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setMagicLinkSent(false);
              setEmail("");
            }}
            className="w-full text-white"
          >
            Use a different email
          </Button>

          <Button
            type="button"
            onClick={() => handleMagicLinkLogin({ preventDefault: () => {} })}
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? "Sending..." : "Resend magic link"}
          </Button>
        </div>

        <div className="text-center text-sm">
          Don&apos;t have an account?{" "}
          <Link to="/auth/signup" className="inline-flex items-center group">
            <span
              className="text-sm font-medium bg-clip-text text-transparent animate-gradient bg-[length:300%_100%] inline-flex items-center"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #ffaa40, #9c40ff, #ffaa40)",
              }}
            >
              Sign Up
              <ChevronRight className="ml-1 size-3.5 stroke-[#9c40ff] transition-transform duration-300 ease-in-out group-hover:translate-x-0.5" />
            </span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleMagicLinkLogin}
      className={cn("flex flex-col gap-6", className)}
      {...props}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Welcome to Artifact</h1>
        {/* <p className="text-balance text-sm text-muted-foreground">
          Enter your email to receive a magic link
        </p> */}
      </div>

      {/* {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )} */}

      {isIncognito && (
        <Alert variant="warning" className="mb-2">
          <Info className="h-4 w-4" />
          <AlertDescription>
            You&apos;re using incognito mode. For the best experience, we
            recommend using social login options below.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6">
        {/* If in incognito mode, emphasize the social login options */}
        {isIncognito ? (
          <>
            <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
              <span className="relative z-10 bg-background px-2 text-muted-foreground">
                Recommended for incognito mode
              </span>
            </div>

            <div className="grid gap-3">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
                <span className="text-foreground">Continue with Google</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGitHubLogin}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-5 w-5 mr-2 fill-black dark:fill-white"
                >
                  <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                </svg>
                <span className="text-foreground">Continue with GitHub</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleLinkedInLogin}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-5 w-5 mr-2"
                  fill="#0077B5"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                <span className="text-foreground">Continue with LinkedIn</span>
              </Button>
            </div>

            <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
              <span className="relative z-10 bg-background px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading || isSubmitting}
                autoComplete="email"
                className="placeholder:text-muted-foreground"
                data-testid={TEST_IDS.AUTH_EMAIL_INPUT}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={
                isLoading || isSubmitting || !email || !isValidEmail(email)
              }
              data-testid={TEST_IDS.AUTH_SUBMIT_BUTTON}
            >
              {isLoading || isSubmitting
                ? "Sending magic link..."
                : "Send magic link"}
            </Button>
          </>
        ) : (
          // Regular login form for non-incognito mode
          <>
            <div className="grid gap-3">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
                <span className="text-foreground">Continue with Google</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGitHubLogin}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-5 w-5 mr-2 fill-black dark:fill-white"
                >
                  <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                </svg>
                <span className="text-foreground">Continue with GitHub</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleLinkedInLogin}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-5 w-5 mr-2"
                  fill="#0077B5"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                <span className="text-foreground">Continue with LinkedIn</span>
              </Button>
            </div>

            <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
              <span className="relative z-10 bg-background px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>

            <div className="grid gap-2">
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading || isSubmitting}
                autoComplete="email"
                className="placeholder:text-muted-foreground"
                data-testid={TEST_IDS.AUTH_EMAIL_INPUT}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={
                isLoading || isSubmitting || !email || !isValidEmail(email)
              }
              data-testid={TEST_IDS.AUTH_SUBMIT_BUTTON}
            >
              {isLoading || isSubmitting
                ? "Sending magic link..."
                : "Send magic link"}
            </Button>
          </>
        )}
      </div>

      <div className="text-center text-sm">
        Don&apos;t have an account?{" "}
        <Link to="/auth/signup" className="inline-flex items-center group">
          <span
            className="text-sm font-medium bg-clip-text text-transparent animate-gradient bg-[length:300%_100%] inline-flex items-center"
            style={{
              backgroundImage:
                "linear-gradient(to right, #ffaa40, #9c40ff, #ffaa40)",
            }}
          >
            Sign Up
            <ChevronRight className="ml-1 size-3.5 stroke-[#9c40ff] transition-transform duration-300 ease-in-out group-hover:translate-x-0.5" />
          </span>
        </Link>
      </div>

      {/* <div className="text-center text-xs text-muted-foreground">
        <Link to="/auth/dev/login" className="hover:text-primary underline">
          Dev Login (Email/Password)
        </Link>
      </div> */}
    </form>
  );
}

export default LoginForm;
